import { useParams } from 'react-router-dom';
import { useCountryGet } from '../hooks/use-country.hook';
import { memo, useMemo } from 'react';
import CountryDetailsComponent from '../components/country-details.component';
import { DetailItem } from '../../../../common/RenderDetails';
import { Country } from '../types/country.types';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { formatDate } from '../../../../../utils/date.util';
import { WithSysAdminPermissionCheck } from '@components/auth/WithSysAdminPermissionCheck';
import PageTitle from '@components/common/PageTitle';

const CountryDetailsContainer = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { data, isLoading, error } = useCountryGet(id ?? '');

  const prepareDetailsData = useMemo(
    () =>
      (dataObj: Country | undefined): DetailItem[] => {
        if (!dataObj) return [];

        const detailItems: DetailItem[] = [];

        // prepare data to display in the details card
        Object.entries(dataObj).forEach(([key, value]) => {
          if (key === 'createdAt' || key === 'updatedAt') {
            detailItems.push({
              label: t(`common.${key}.label`),
              value: formatDate(value),
            });
            return;
          }

          if (key === 'id') {
            detailItems.push({
              label: t(`common.${key}.label`),
              value: value.toString(),
            });
            return;
          }

          detailItems.push({
            label: t(`countries.${key}.label`),
            value: String(value),
          });
        });

        return detailItems;
      },
    [data?.data, t]
  );

  if (!id) {
    return (
      <ErrorFallback
        details={t('crud.details.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.details.title', { entity: t('countries.title') })} />
        <HeaderRow
          title={t('crud.details.title', {
            entity: t('countries.title'),
          })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('crud.details.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.details.title', { entity: t('countries.title') })} />
      <HeaderRow
        title={t('crud.details.title', { entity: t('countries.title') })}
        showBackButton={true}
        isSysAdmin={true}
      />
      <div className="row mt-3">
        <CountryDetailsComponent
          data={prepareDetailsData(data?.data)}
          success={data?.success ?? false}
          isLoading={isLoading}
          error={error}
        />
      </div>
    </>
  );
};

export default WithSysAdminPermissionCheck(memo(CountryDetailsContainer), 'country.view');
